import { html } from 'lit';
import { getProductRegistryApi } from '../../modules/product-registry/api/product-registry-api.js';
import { isSame } from '../../utility/strings/strings-utils.js';
import { registerMemberWorkspaceModule } from '../../pages/app-members/workspace/member-workspace-modules.js';
import {
  canOpenProductsList,
  canOpenReportsList,
  navigateTo,
  productCreateHref,
  productHref,
  productsListHref,
  reportCreateHref,
  reportHref,
  reportsListHref,
} from '../../modules/product-registry/product-registry-navigation.js';
import {
  PRODUCTS_TAB_ID,
  PRODUCTS_TAB_LABEL,
  REPORTS_TAB_ID,
  REPORTS_TAB_LABEL,
} from '../../modules/product-registry/product-registry-workspace-tabs.js';
import type {
  ProductCreateDetail,
  ProductOpenDetail,
} from '../../modules/product-registry/components/product-registry-products.js';
import type {
  ReportCreateDetail,
  ReportOpenDetail,
} from '../../modules/product-registry/components/product-registry-reports.js';

/**
 * The Product Registry's workspace tabs (imported by domains/index.ts, which registers every
 * domain's tabs at once).
 *
 * THE COMPANY IS THE ONLY HOST, since [D-127]. A register belongs to the company's Prime System
 * IP, held on a site the system creates and nobody ever sees, so there is no visible site for a
 * site-level view of it to scope to. The two site registrations that used to sit below these were
 * deleted rather than gated, and deleting them is the honest end state: a site's grant carries the
 * SITE's own id as its domain instance id now that the supplier type owns nothing, so a gated tab
 * would have rendered a list against a register that does not exist and shown an empty table with
 * no error - a screen that looks like a company with no products rather than like a bug
 * (`@PIP-REG-002`, and `@PR-WORKSPACE-003` retired).
 *
 * THE GATE IS THE PAIR, NOT THE NEW TYPE. A company holds no operation type of its own; it
 * inherits them from its ACTIVE, VISIBLE sites, and the Prime System IP site is neither listed
 * nor returned in the `items` the inheritance read walks
 * (`member-workspace-store.loadInheritedDomains`), so the company never inherits
 * Prime.System.IntellectualProperty and a tab keyed on it would never appear. "Any active site
 * holds an active supplier or manufacturer scope" is precisely the condition under which D-127
 * keeps the register alive, so the pair IS the register's liveness, read from data the workspace
 * already has. `inheritedDomains` is an `.some()` over the list, so naming both is an OR.
 */
const PRIME_SYSTEM_SUPPLIER = 'Prime.System.Supplier';
const PRIME_SYSTEM_MANUFACTURER = 'Prime.System.Manufacturer';

/** Either classification means the company's register is live (D-127 rule 3). */
const REGISTER_CONSUMER_DOMAINS = [
  PRIME_SYSTEM_SUPPLIER,
  PRIME_SYSTEM_MANUFACTURER,
];

/**
 * THE INACTIVE REGISTER, FOR AGWA STAFF ONLY (D-146). When the pair above closes the register, the
 * company's own people lose these tabs, but AGWA staff are never refused a read of it, and the
 * architect asked to reach one "via its test page". So the tabs also show when the viewer has
 * all-members reach and the company holds a register in any state. The access call is asked to
 * include inactive registers, which the server honours for staff alone; a member's answer never
 * names an inactive register, so this says no for them.
 *
 * Both tabs ask at once, so one read answers both: the promise is shared while it is in flight.
 */
const staffReadInFlight = new Map<string, Promise<boolean>>();

function staffReachRegisterOf(groupId: string): Promise<boolean> {
  const pending = staffReadInFlight.get(groupId);
  if (pending) return pending;
  const read = getProductRegistryApi()
    .getAccess(true)
    .then(
      (result) =>
        result.success &&
        result.data.hasAllMembersAccess &&
        result.data.accessibleOwners.some((owner) =>
          isSame(owner.loginGroupId, groupId)
        )
    )
    .finally(() => staffReadInFlight.delete(groupId));
  staffReadInFlight.set(groupId, read);
  return read;
}

/** The company's products, hosted on the company workspace. */
registerMemberWorkspaceModule({
  id: PRODUCTS_TAB_ID,
  label: PRODUCTS_TAB_LABEL,
  icon: 'boxes',
  order: 60,
  inheritedDomains: REGISTER_CONSUMER_DOMAINS,
  alsoShowWhen: staffReachRegisterOf,
  async load() {
    await import('../../modules/product-registry/components/product-registry-products.js');
  },
  render: (context) => html`
    <product-registry-products
      company-id=${context.group?.id ?? ''}
      ?link-module=${canOpenProductsList()}
      @product-open=${(e: CustomEvent<ProductOpenDetail>) =>
        navigateTo(productHref(e.detail.productId))}
      @product-create=${(e: CustomEvent<ProductCreateDetail>) =>
        navigateTo(productCreateHref(e.detail.ownerId))}
      @module-open=${() => navigateTo(productsListHref())}
    ></product-registry-products>
  `,
});

/** The company's product test reports, beside its products tab. */
registerMemberWorkspaceModule({
  id: REPORTS_TAB_ID,
  label: REPORTS_TAB_LABEL,
  icon: 'clipboard-check',
  order: 61,
  inheritedDomains: REGISTER_CONSUMER_DOMAINS,
  alsoShowWhen: staffReachRegisterOf,
  async load() {
    await import('../../modules/product-registry/components/product-registry-reports.js');
  },
  render: (context) => html`
    <product-registry-reports
      company-id=${context.group?.id ?? ''}
      ?link-module=${canOpenReportsList()}
      @report-open=${(e: CustomEvent<ReportOpenDetail>) =>
        navigateTo(reportHref(e.detail.routeSegment, e.detail.reportId))}
      @report-create=${(e: CustomEvent<ReportCreateDetail>) =>
        navigateTo(reportCreateHref(e.detail))}
      @module-open=${() => navigateTo(reportsListHref())}
    ></product-registry-reports>
  `,
});
